'use client';

import Image from 'next/image';
import {
  SyntheticEvent,
  FC,
  memo,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { useInView } from '@/src/hooks/useInView';
import { PromoLabel } from '@/src/components/PromoLabel';

// Типы для элементов, которые могут содержать промо
interface PromoItem {
  __typename?:
    | 'Style'
    | 'PhotoSession'
    | 'Effect'
    | 'CombiVideo'
    | 'StoryLine'
    | 'VideoLook'
    | 'VideoStyle'
    | 'VideoVeo3';
  id: string;
}

const styles = {
  styleItem: 'shrink-0 relative overflow-hidden rounded-lg aspect-square',
  videoItem: 'aspect-4/5',
  horizontalVideoItem: 'aspect-[16/9]',
  preview: 'w-full h-full object-cover object-top',
  previewVideo: 'w-full h-[360px] object-cover object-top pointer-events-none',
  tag: 'absolute top-1 right-1',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-sm',
  gradientVideo: 'bg-gradient-xl',
  info: 'absolute left-3 bottom-3',
  infoVideo: 'bottom-6 left-1 right-1',
  styleName: 'text-sm font-medium text-left',
  videoName: 'text-xl font-bold text-center',
};

type TPackPreviewProps = {
  id: string;
  index: number;
  isNew: boolean;
  isPopular: boolean;
  image: string;
  name?: string;
  handleClickImage: (event: SyntheticEvent) => void;
  isVideo: boolean;
  isSmall?: boolean;
  isHorizontalVideo?: boolean;
  // Добавляем данные для PromoLabel
  __typename?:
    | 'Style'
    | 'PhotoSession'
    | 'Effect'
    | 'CombiVideo'
    | 'StoryLine'
    | 'VideoLook'
    | 'VideoStyle'
    | 'VideoVeo3';
};

export const PackPreviewSeeAll: FC<TPackPreviewProps> = memo(
  ({
    id,
    index,
    isNew,
    isPopular,
    image,
    name,
    handleClickImage,
    isVideo,
    isSmall,
    isHorizontalVideo,
    __typename,
  }) => {
    const [mediaLoaded, setMediaLoaded] = useState(false);

    const [hasStarted, setHasStarted] = useState(false);
    const [refForPlay, isInViewport] = useInView<HTMLButtonElement>({
      rootMargin: '0px',
      threshold: 0.1,
    });

    const videoRef = useRef<HTMLVideoElement>(null);

    // Создаем объект для PromoLabel
    const promoItem: PromoItem = useMemo(
      () => ({
        __typename,
        id,
      }),
      [__typename, id],
    );

    useEffect(() => {
      if (!hasStarted && isInViewport && videoRef.current) {
        videoRef.current.play().catch();
        setHasStarted(true);
      }
    }, [isInViewport, hasStarted]);

    return (
      <button
        ref={refForPlay}
        className={twMerge(
          styles.styleItem,
          isVideo && !isSmall && styles.videoItem,
          isHorizontalVideo && styles.horizontalVideoItem,
        )}
        data-style-id={id}
        data-style-ind={index + 1}
        onClick={handleClickImage}
      >
        {!mediaLoaded && (
          <div className="absolute z-10 inset-0 animate-skeleton-pulse"></div>
        )}
        {isNew && (
          <Image
            width={42}
            height={23}
            src="/icons/label-new.svg"
            alt="tag"
            className={styles.tag}
          />
        )}
        {isPopular && !isNew && (
          <Image
            width={42}
            height={23}
            src="/icons/label-top.svg"
            alt="tag"
            className={styles.tag}
          />
        )}
        <PromoLabel item={promoItem} />
        {image &&
          (isVideo ? (
            <video
              key={image}
              ref={videoRef}
              preload="metadata"
              loop
              muted
              playsInline
              webkit-playsinline="true"
              className={styles.preview}
              onLoadedData={() => setMediaLoaded(true)}
            >
              <source src={image} />
            </video>
          ) : (
            <Image
              width={152}
              height={180}
              src={image}
              alt={name || 'style preview'}
              className={styles.preview}
              quality={95}
              loading="lazy"
              onLoad={() => setMediaLoaded(true)}
            />
          ))}
        <div className={styles.gradient} />
        <div className={styles.info}>
          <div className={styles.styleName}>{name}</div>
        </div>
      </button>
    );
  },
);

PackPreviewSeeAll.displayName = 'PackPreviewSeeAll';
